import { GET_ORGANIZATION } from "@/actions/GET_ORGANIZATION.ts";
import { Language } from "@/components/Inputs/LanguagePicker/Language.ts";
import { WarningToast } from "@/components/Notifications/WarningToast.tsx";
import { Organization, PaymentProviderEnum } from "@/helpers/types/types.ts";
import { getLocale } from "@/stores/ApolloClient/helpers/getLocale.ts";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface GetOrganizationResponse {
  currentOrganization: Organization;
}

export const useGetOrganization = () => {
  const { data, loading, error, refetch } = useQuery<GetOrganizationResponse>(GET_ORGANIZATION);

  const { i18n, t } = useTranslation();
  const locale = getLocale(i18n.language as Language);

  // The backend resolves the top-level privacyPolicy/termsAndConditions in the request's
  // `locale` header, and `translations` never contains its default locale (hr). Without a
  // refetch, switching e.g. en -> hr finds no "hr" translation and falls back to the
  // top-level text that was fetched in en.
  const fetchedLocale = useRef(locale);
  useEffect(() => {
    if (fetchedLocale.current === locale) return;
    fetchedLocale.current = locale;
    void refetch();
  }, [locale, refetch]);

  useEffect(() => {
    if (error) {
      console.error("GET_ORGANIZATION failed:", error.message);
      toast.custom(<WarningToast message={t("error")} />);
    }
  }, [error, t]);

  const paymentEnabled = useMemo(() => {
    return (
      data?.currentOrganization?.paymentType === PaymentProviderEnum.WsPay ||
      data?.currentOrganization?.paymentType === PaymentProviderEnum.WsPayPer ||
      data?.currentOrganization?.paymentType === PaymentProviderEnum.M2Pay
    );
  }, [data?.currentOrganization?.paymentType]);

  // Card details are entered in our form only for WsPayPer; other providers collect them on their redirect page
  const hasEmbeddedCardForm = data?.currentOrganization?.paymentType === PaymentProviderEnum.WsPayPer;

  return {
    organization: data?.currentOrganization,
    getOrganizationLoading: loading,
    privacyPolicy:
      data?.currentOrganization?.translations?.find((ot) => ot.language === locale)?.privacyPolicy ||
      data?.currentOrganization?.privacyPolicy,
    termsAndConditions:
      data?.currentOrganization?.translations?.find((ot) => ot.language === locale)?.termsAndConditions ||
      data?.currentOrganization?.termsAndConditions,
    logoUrl: data?.currentOrganization?.logoUrl,
    organizationName: data?.currentOrganization?.name,
    paymentType: data?.currentOrganization?.paymentType,
    paymentEnabled,
    hasEmbeddedCardForm,
    gtmKey: data?.currentOrganization?.gtmKey,
  };
};
